/**
 * The destination steps' parts (AMO-166): the editor for Open a chat, Notify,
 * Save to space and Send email, and on a run a link to what each delivered
 * (the chat, the document) or who it reached.
 *
 * @module components/workflows/DestinationParts
 */
import { WORKFLOW_LIMITS, type WorkflowNode, type WorkflowStepDelivery } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import { DownloadIcon, FileSpreadsheetIcon, FileTextIcon, MessageSquareIcon } from "lucide-react";

import { spaceDocumentDownloadUrl, useOrgSpaces } from "~/state/spaces";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { PeoplePicker, type MemberChoice } from "./PeoplePicker";

type DestinationNode = Extract<WorkflowNode, { type: "chat" | "notify" | "save-file" | "email" }>;

/** What Save to space makes (AMO-300). */
const SAVE_FORMATS = [
  { value: "markdown", label: "Document (Markdown)" },
  { value: "excel", label: "Excel workbook" },
  { value: "word", label: "Word document" },
] as const;

const WHAT_REACHES = "It takes what is linked into it, as Output shows it.";

function TextSetting({
  label,
  value,
  placeholder,
  maxLength,
  hint,
  onChange,
  name,
  field,
}: {
  readonly label: string;
  readonly value: string;
  readonly placeholder: string;
  readonly maxLength: number;
  readonly hint: string;
  readonly onChange: (value: string) => void;
  readonly name: string;
  /** The step field it edits, as a revision's diff names it: shared while the person types in it. */
  readonly field: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium">{label}</span>
      <Input
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event) => onChange(event.currentTarget.value)}
        {...{ [`data-workflow-${name}`]: "" }}
        data-step-field={field}
      />
      <span className="block text-[11px] leading-snug text-muted-foreground">{hint}</span>
    </label>
  );
}

/** One of the organization's spaces, for a step that reads or writes one. */
export function SpaceChoice({
  organizationId,
  spaceId,
  onChange,
  hint = "Each run saves a new document there, as the person the run acts for, who must be on the space's team.",
  name = "save-space",
}: {
  readonly organizationId: string | null;
  readonly spaceId: string;
  readonly onChange: (spaceId: string) => void;
  readonly hint?: string;
  /** Names the trigger for tests: `data-workflow-<name>`. */
  readonly name?: string;
}) {
  const { spaces, isPending } = useOrgSpaces(organizationId);
  const chosen = spaces.find((space) => space.id === spaceId);
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium">Space</span>
      <Select value={spaceId} onValueChange={(value) => (typeof value === "string" ? onChange(value) : undefined)}>
        <SelectTrigger size="sm" aria-label="Space" {...{ [`data-workflow-${name}`]: "" }}>
          <span className="truncate text-xs">
            {chosen?.name ?? (spaceId ? "A space you cannot see" : isPending ? "Loading spaces…" : "Choose a space")}
          </span>
        </SelectTrigger>
        <SelectContent>
          {spaces.map((space) => (
            <SelectItem key={space.id} value={space.id}>
              {space.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="block text-[11px] leading-snug text-muted-foreground">{hint}</span>
    </label>
  );
}

/** Edits one destination step while the chart is being edited. */
export function DestinationStepEditor({
  node,
  organizationId,
  members,
  onChange,
}: {
  readonly node: DestinationNode;
  readonly organizationId: string | null;
  readonly members: ReadonlyArray<MemberChoice> | null;
  readonly onChange: (next: DestinationNode) => void;
}) {
  switch (node.type) {
    case "chat":
      return (
        <>
          <TextSetting
            label="Chat title (optional)"
            value={node.config.title}
            placeholder="The workflow's name and the date"
            maxLength={WORKFLOW_LIMITS.labelLength}
            hint="Opens a chat for the person the run acts for, its first message the results, to follow up on. No AI turn runs, so it costs nothing."
            onChange={(title) => onChange({ ...node, config: { ...node.config, title } })}
            field="config.title"
            name="chat-title"
          />
          <p className="text-[11px] leading-snug text-muted-foreground">{WHAT_REACHES}</p>
        </>
      );
    case "notify":
      return (
        <>
          <PeoplePicker
            label="Who gets the notice"
            role="recipient"
            people={node.config.recipientEmails}
            members={members}
            limit={WORKFLOW_LIMITS.recipients}
            note="Members only: anyone who has left by the time the run gets here is skipped."
            onChange={(recipientEmails) => onChange({ ...node, config: { ...node.config, recipientEmails: [...recipientEmails] } })}
          />
          <TextSetting
            label="Title (optional)"
            value={node.config.title}
            placeholder="“<workflow> finished”"
            maxLength={160}
            hint="The notice holds the results and opens the run."
            onChange={(title) => onChange({ ...node, config: { ...node.config, title } })}
            field="config.title"
            name="notify-title"
          />
          <p className="text-[11px] leading-snug text-muted-foreground">{WHAT_REACHES}</p>
        </>
      );
    case "save-file": {
      const format = node.config.format ?? "markdown";
      const excel = format === "excel";
      return (
        <>
          <SpaceChoice
            organizationId={organizationId}
            spaceId={node.config.spaceId}
            onChange={(spaceId) => onChange({ ...node, config: { ...node.config, spaceId } })}
          />
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Save as</span>
            <Select
              value={format}
              onValueChange={(value) =>
                onChange({ ...node, config: { ...node.config, format: value === "excel" || value === "word" ? value : "markdown" } })
              }
            >
              <SelectTrigger size="sm" aria-label="Save as" data-workflow-save-format="">
                <span className="truncate text-xs">{SAVE_FORMATS.find((choice) => choice.value === format)?.label}</span>
              </SelectTrigger>
              <SelectContent>
                {SAVE_FORMATS.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <TextSetting
            label={excel ? "Workbook name (optional)" : "Document name (optional)"}
            value={node.config.title}
            placeholder="The workflow's name and the date"
            maxLength={WORKFLOW_LIMITS.labelLength}
            hint={
              excel
                ? "An Excel workbook, one per run: a sheet for each table linked in (a spreadsheet Intake read, rows an Ingestion step read). With no table, one sheet holds the results a line per row. It opens as a sheet in the space, and downloads as .xlsx."
                : format === "word"
                  ? "A Word document, one per run: the results with headings, lists and the tables linked in as Word tables. It opens as a page in the space, and the run links its .docx to download."
                  : "A Markdown document, one per run."
            }
            onChange={(title) => onChange({ ...node, config: { ...node.config, title } })}
            field="config.title"
            name="save-title"
          />
          <p className="text-[11px] leading-snug text-muted-foreground">{WHAT_REACHES}</p>
        </>
      );
    }
    case "email":
      return (
        <>
          <PeoplePicker
            label="Who gets the email"
            role="recipient"
            people={node.config.recipientEmails}
            members={members}
            limit={WORKFLOW_LIMITS.recipients}
            note="Members of this organization only. It is sent as the person the run acts for, says which workflow sent it, and counts toward the organization's 100 emails a day."
            onChange={(recipientEmails) => onChange({ ...node, config: { ...node.config, recipientEmails: [...recipientEmails] } })}
          />
          <TextSetting
            label="Subject (optional)"
            value={node.config.subject}
            placeholder="The workflow's name and the date"
            maxLength={200}
            hint="The email's body is the results."
            onChange={(subject) => onChange({ ...node, config: { ...node.config, subject } })}
            field="config.subject"
            name="email-subject"
          />
          <p className="text-[11px] leading-snug text-muted-foreground">{WHAT_REACHES}</p>
        </>
      );
  }
}

/** On a run: where a destination step delivered, as a link or a list of who it reached. */
export function DeliveryLink({ delivery, org }: { readonly delivery: WorkflowStepDelivery; readonly org: string }) {
  switch (delivery.kind) {
    case "chat":
      return (
        <Button
          size="sm"
          variant="outline"
          render={<Link to="/$org/$threadId" params={{ org, threadId: delivery.threadId }} />}
          data-workflow-delivery="chat"
        >
          <MessageSquareIcon />
          Open the chat
        </Button>
      );
    case "file":
      return (
        <div className="flex flex-wrap gap-1.5">
          <Button
            size="sm"
            variant="outline"
            render={<Link to="/$org/spaces/$spaceId" params={{ org, spaceId: delivery.spaceId }} search={{ doc: delivery.docId }} />}
            data-workflow-delivery="file"
          >
            {delivery.docId.startsWith("sheet:") ? <FileSpreadsheetIcon /> : <FileTextIcon />}
            Open “{delivery.title}”
          </Button>
          {delivery.download === "word" ? (
            <Button
              size="sm"
              variant="outline"
              render={<a href={spaceDocumentDownloadUrl(delivery.spaceId, delivery.docId, true)} download />}
              data-workflow-delivery-download="word"
            >
              <DownloadIcon />
              Download Word
            </Button>
          ) : null}
        </div>
      );
    case "notice":
    case "email":
      return (
        <p className="text-[12.5px] text-foreground" data-workflow-delivery={delivery.kind}>
          {delivery.kind === "email" ? "Emailed" : "Notified"} {delivery.recipients.join(", ") || "nobody"}.
          {delivery.kind === "email" && delivery.missed.length > 0 ? (
            <span className="block text-muted-foreground">Not reached: {delivery.missed.join(", ")}.</span>
          ) : null}
        </p>
      );
  }
}
